import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Loader2, MapPin, StickyNote } from 'lucide-react';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Card } from '@shared/ui/containers/Card';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Button } from '@shared/ui/primitives/Button';
import { toast } from '@shared/ui/feedback/Toast';
import { useBrandImagery } from '@shared/hooks/useBrandImagery';
import { pageCopy } from '@shared/lib/pageCopy';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { toApiError } from '@shared/api/errors';
import { cn } from '@shared/lib/cn';
import { fetchEvents, isRsvpClosed, rsvpEvent, type EventSummary } from './api';
import { GuestRsvpSheet, type GuestDraft } from './GuestRsvpSheet';

type GuestSheetState = { event: EventSummary; mode: 'rsvp' | 'view' };

const DATE_FMT = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const TIME_FMT = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });

function isSameCalendarDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function formatEventSchedule(event: EventSummary, start: Date, end: Date | null): string {
  const startDate = DATE_FMT.format(start);

  if (event.all_day) {
    if (end && !isSameCalendarDay(start, end)) {
      return `${startDate} – ${DATE_FMT.format(end)} · All day`;
    }
    return `${startDate} · All day`;
  }

  const startLabel = `${startDate} · ${TIME_FMT.format(start)}`;
  if (!end) return startLabel;

  if (isSameCalendarDay(start, end)) {
    return `${startLabel} – ${TIME_FMT.format(end)}`;
  }

  return `${startLabel} – ${DATE_FMT.format(end)} · ${TIME_FMT.format(end)}`;
}

function googleMapsUrl(location: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.trim())}`;
}

type Rsvp = 'yes' | 'no';

type RsvpResult = {
  status: Rsvp;
  pendingApproval?: boolean;
};

/**
 * Events — Fancourt layout: a photo header over a feed of image-led event
 * cards with inline RSVP actions.
 */
export function Events(): JSX.Element {
  const copy = pageCopy().events;
  const queryClient = useQueryClient();
  const { heroImage } = useBrandImagery();
  const [rsvpResult, setRsvpResult] = useState<RsvpResult | null>(null);
  const [guestSheet, setGuestSheet] = useState<GuestSheetState | null>(null);

  const range = useMemo(() => {
    // Include past (ended) events as well as upcoming ones.
    const from = new Date();
    from.setFullYear(from.getFullYear() - 5);
    const to = new Date();
    to.setMonth(to.getMonth() + 6);
    return { from: from.toISOString(), to: to.toISOString() };
  }, []);

  const eventsQuery = useQuery({
    queryKey: ['events'],
    queryFn: () => fetchEvents(range.from, range.to),
  });

  const rsvp = useMutation({
    mutationFn: ({ id, status, guests }: { id: string; status: Rsvp; guests?: GuestDraft[] }) =>
      rsvpEvent(id, status, guests),
    onSuccess: (registration, { status }) => {
      void queryClient.invalidateQueries({ queryKey: ['events'] });
      setGuestSheet(null);
      setRsvpResult({
        status,
        pendingApproval:
          status === 'yes' && registration?.approval_status === 'pending_approval',
      });
    },
    onError: (error) => toast.danger('Could not update RSVP', toApiError(error).message),
  });

  const now = Date.now();
  const hasEnded = (event: EventSummary) =>
    new Date(event.ends_at ?? event.starts_at).getTime() < now;
  const events = [...(eventsQuery.data ?? [])].sort((a, b) => {
    const aEnded = hasEnded(a);
    const bEnded = hasEnded(b);
    // Upcoming events first (soonest first); ended events after (most recent first).
    if (aEnded !== bEnded) return aEnded ? 1 : -1;
    return aEnded
      ? b.starts_at.localeCompare(a.starts_at)
      : a.starts_at.localeCompare(b.starts_at);
  });

  return (
    <div className="min-h-full bg-paper">
      {/* ── Photo header ─────────────────────────────────────────── */}
      <HeroHeader
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
        backgroundImage={heroImage}
      />

      <div className="flex flex-col gap-4 px-5 pt-5 pb-safe-or-6">
        {eventsQuery.isLoading ? (
          <>
            <Skeleton className="h-72" />
            <Skeleton className="h-72" />
          </>
        ) : eventsQuery.isError ? (
          <ErrorState onRetry={() => eventsQuery.refetch()} />
        ) : events.length === 0 ? (
          <EmptyState
            icon={<CalendarDays className="h-7 w-7" strokeWidth={1.5} />}
            title={copy.emptyTitle}
            description={copy.emptyDescription}
          />
        ) : (
          events.map((event) => (
            <EventCard
              key={event.event_id}
              event={event}
              pendingStatus={
                rsvp.isPending && rsvp.variables?.id === event.event_id
                  ? rsvp.variables.status
                  : null
              }
              onRsvp={(status) => {
                if (status === 'yes' && event.allow_guests) {
                  setGuestSheet({ event, mode: 'rsvp' });
                  return;
                }
                rsvp.mutate({ id: event.event_id, status });
              }}
              onViewRsvpDetails={() => setGuestSheet({ event, mode: 'view' })}
            />
          ))
        )}
      </div>

      <GuestRsvpSheet
        open={guestSheet !== null}
        event={guestSheet?.event ?? null}
        mode={guestSheet?.mode ?? 'rsvp'}
        submitting={rsvp.isPending}
        onOpenChange={(open) => !open && setGuestSheet(null)}
        onSubmit={(guests) => {
          if (!guestSheet) return;
          rsvp.mutate({ id: guestSheet.event.event_id, status: 'yes', guests });
        }}
      />
      <RsvpResultSheet result={rsvpResult} onClose={() => setRsvpResult(null)} />
    </div>
  );
}

function EventCard({
  event,
  pendingStatus,
  onRsvp,
  onViewRsvpDetails,
}: {
  event: EventSummary;
  pendingStatus: Rsvp | null;
  onRsvp: (status: Rsvp) => void;
  onViewRsvpDetails: () => void;
}): JSX.Element {
  const start = parseEventDate(event.starts_at);
  const end = parseEventDate(event.ends_at ?? '');
  const now = Date.now();
  const startMs = start?.getTime() ?? null;
  const endMs = end?.getTime() ?? startMs;
  // RSVP is no longer possible once the event has started.
  const hasStarted = startMs !== null && startMs <= now;
  const hasEnded = endMs !== null && endMs < now;
  const rsvpClosed = isRsvpClosed(event) || hasStarted;
  const hasRsvp = event.rsvp_status === 'yes';

  return (
    <Card asChild noGrain className="overflow-hidden rounded-xl p-0">
      <article>
      {/* Cover */}
      <div className="relative aspect-[16/9] w-full bg-sunken">
        {event.cover_image_url ? (
          <img
            src={event.cover_image_url}
            alt=""
            className={cn('h-full w-full object-cover', hasEnded && 'grayscale')}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-primary-50">
            <CalendarDays className="h-8 w-8 text-primary-600" strokeWidth={1.25} />
          </div>
        )}
        {event.category ? (
          <span className="absolute left-3 top-3 rounded-pill bg-black/65 px-2.5 py-1 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-white backdrop-blur">
            {event.category}
          </span>
        ) : null}
        {hasEnded ? (
          <span className="absolute right-3 top-3 rounded-pill bg-black/65 px-2.5 py-1 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-white backdrop-blur">
            Ended
          </span>
        ) : hasStarted ? (
          <span className="absolute right-3 top-3 flex items-center gap-1.5 rounded-pill bg-emerald-600/90 px-2.5 py-1 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-white backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-white" aria-hidden />
            Happening now
          </span>
        ) : null}
      </div>

      {/* Body */}
      <div className="p-4">
        <h2 className="font-display text-headline text-ink">{event.title}</h2>
        <p className="mt-1.5 flex items-center gap-1.5 text-body-sm text-ink-secondary">
          <CalendarDays className="h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.5} />
          {start ? formatEventSchedule(event, start, end) : 'Date to be confirmed'}
        </p>
        {event.location ? (
          <p className="mt-1 flex items-start gap-1.5 text-body-sm text-ink-secondary">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.5} />
            <a
              href={googleMapsUrl(event.location)}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-pre-line font-medium text-primary-700 underline-offset-2 hover:underline"
            >
              {event.location}
            </a>
          </p>
        ) : null}
        {event.description ? (
          <EventDescription text={event.description} />
        ) : null}

        {event.rsvp_required ? (
          <>
            {hasStarted ? (
              // Once the event has started RSVP is no longer possible — show its state instead.
              <p className="mt-3 text-body-sm font-medium text-ink-secondary">
                {hasEnded ? 'This event has ended.' : 'This event is in progress.'}
              </p>
            ) : (
              <>
                <div className="mt-3 flex gap-2">
                  {(['yes', 'no'] as Rsvp[]).map((status) => {
                    const active = event.rsvp_status === status;
                    const loading = pendingStatus === status;
                    const label = status === 'yes' ? 'Going' : 'Decline';
                    return (
                      <button
                        key={status}
                        type="button"
                        disabled={pendingStatus !== null || rsvpClosed}
                        aria-busy={loading}
                        aria-disabled={rsvpClosed}
                        onClick={() => {
                          if (!active && !pendingStatus && !rsvpClosed) {
                            onRsvp(status);
                          }
                        }}
                        className={rsvpButtonClass(status, active, rsvpClosed)}
                      >
                        {loading ? (
                          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} aria-hidden />
                        ) : (
                          <>
                            {label}
                          </>
                        )}
                      </button>
                    );
                  })}
                </div>
                {rsvpClosed ? (
                  <p className="mt-2 text-caption text-ink-muted">RSVP has closed.</p>
                ) : null}
              </>
            )}
            {hasRsvp ? (
              <button
                type="button"
                onClick={onViewRsvpDetails}
                className="mt-3 w-full rounded-md border border-hairline-strong bg-card py-2.5 text-body-sm font-medium text-ink-secondary transition-colors hover:bg-sunken"
              >
                RSVP details
              </button>
            ) : null}
          </>
        ) : null}
      </div>
      </article>
    </Card>
  );
}

function EventDescription({ text }: { text: string }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const node = descriptionRef.current;
    if (!node || expanded) return;
    setIsOverflowing(node.scrollHeight > node.clientHeight + 1);
  }, [text, expanded]);

  const showToggle = isOverflowing || expanded;

  return (
    <div className="mt-2 flex items-start gap-1.5">
      <StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" strokeWidth={1.5} aria-hidden />
      <div className="min-w-0 flex-1">
        <p
          ref={descriptionRef}
          className={cn(
            'whitespace-pre-line text-body-sm text-ink-secondary',
            !expanded && 'line-clamp-4',
          )}
        >
          {text}
        </p>
        {showToggle ? (
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="mt-1 text-body-sm font-medium text-primary-700 underline-offset-2 hover:underline"
          >
            {expanded ? 'Show less' : 'View more'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function rsvpButtonClass(status: Rsvp, active: boolean, closed: boolean): string {
  const base =
    'flex flex-1 items-center justify-center gap-1.5 rounded-md py-2.5 text-body-sm font-medium transition-colors disabled:cursor-not-allowed';

  if (closed) {
    return `${base} bg-sunken text-ink-muted opacity-60`;
  }

  if (status === 'yes') {
    return (
      base +
      ' disabled:opacity-70' +
      (active
        ? ' bg-primary-700 text-white'
        : ' bg-card hairline text-ink-secondary hover:bg-sunken')
    );
  }

  return (
    base +
    ' disabled:opacity-70' +
    (active
      ? ' bg-danger text-white'
      : ' border border-danger/30 bg-card text-danger hover:bg-danger/8')
  );
}

function rsvpResultCopy(result: RsvpResult): { title: string; message: string } {
  if (result.status === 'no') {
    return {
      title: 'Attendance declined',
      message: "Thanks for letting us know you can't make it.",
    };
  }

  if (result.pendingApproval) {
    return {
      title: 'RSVP submitted',
      message: "We've received your RSVP. We'll notify you once it's been approved.",
    };
  }

  return {
    title: "You're going!",
    message: "You're registered for this event. We look forward to seeing you there.",
  };
}

function RsvpResultSheet({
  result,
  onClose,
}: {
  result: RsvpResult | null;
  onClose: () => void;
}): JSX.Element {
  const open = result !== null;
  const copy = result ? rsvpResultCopy(result) : null;

  return (
    <BottomSheet open={open} onOpenChange={(next) => !next && onClose()}>
      <div className="flex flex-col items-center gap-4 pb-6 pt-2 text-center">
        <div>
          <h2 className="font-display text-headline text-ink">{copy?.title}</h2>
          <p className="mt-1 text-body-sm text-ink-secondary">{copy?.message}</p>
        </div>
        <Button variant="primary" width="full" onClick={onClose}>
          Done
        </Button>
      </div>
    </BottomSheet>
  );
}

function parseEventDate(iso: string): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}
